import React, { useState, useEffect } from 'react';
import { Send, MessageSquare } from 'lucide-react';

const IsometricTwoWayTexting = () => {
    // Standard Isometric rotation from IsometricCalendar
    const sceneRotation = {
        transform: 'rotateX(55deg) rotateZ(45deg)',
        transformStyle: 'preserve-3d' as const
    };

    const [chatState, setChatState] = useState<'initial' | 'typing' | 'responded'>('initial');

    useEffect(() => {
        const sequence = async () => {
            // Wait a moment before starting animation
            await new Promise(r => setTimeout(r, 1000));

            // Start typing
            setChatState('typing');
            await new Promise(r => setTimeout(r, 2000));

            // Show response and stay there
            setChatState('responded');
        };

        sequence();
    }, []);

    return (
        <div className="w-full h-[500px] flex items-center justify-center overflow-visible">
            <div
                className="relative w-[300px] h-[500px]"
                style={sceneRotation}
            >
                {/* Floating Animation Wrapper */}
                <div className="w-full h-full animate-float" style={{ transformStyle: 'preserve-3d' }}>
                    <style>
                        {`
                            @keyframes float {
                                0%, 100% { transform: translateZ(10px); }
                                50% { transform: translateZ(30px); }
                            }
                        `}
                    </style>

                    {/* Phone Body Thickness (Side Layers) */}
                    {Array.from({ length: 25 }).map((_, i) => (
                        <div
                            key={i}
                            className="absolute top-0 left-0 w-full h-full rounded-[40px] bg-gray-900"
                            style={{
                                transform: `translateZ(-${i + 1}px)`,
                            }}
                        />
                    ))}

                    {/* Phone Body (Top Face) */}
                    <div
                        className="absolute top-0 left-0 w-full h-full rounded-[40px] bg-gray-900 p-4"
                        style={{
                            transform: 'translateZ(0px)',
                            boxShadow: 'inset 0 0 0 2px rgba(255,255,255,0.1)'
                        }}
                    >
                        {/* Phone Bezel/Screen Container */}
                        <div className="w-full h-full bg-black rounded-[32px] overflow-hidden relative border border-gray-800 flex flex-col">
                            {/* Status Bar Mock */}
                            <div className="h-6 w-full flex justify-between px-4 items-center mt-2 flex-shrink-0">
                                <div className="text-[10px] text-white font-medium">9:41</div>
                                <div className="flex gap-1">
                                    <div className="w-3 h-3 rounded-full bg-white/20"></div>
                                    <div className="w-3 h-3 rounded-full bg-white/20"></div>
                                </div>
                            </div>

                            {/* App Header */}
                            <div className="px-5 mt-4 mb-4 flex-shrink-0 border-b border-gray-800 pb-2">
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-teal-500/20 flex items-center justify-center border border-teal-500/30">
                                        <div className="text-teal-400 font-bold text-xs">RM</div>
                                    </div>
                                    <div>
                                        <div className="text-gray-200 text-xs font-semibold">Robynne M.</div>
                                        <div className="text-gray-500 text-[10px]">Active now</div>
                                    </div>
                                </div>
                            </div>

                            {/* Chat Content */}
                            <div className="flex-1 px-4 flex flex-col gap-4 overflow-hidden relative">

                                {/* Message 1 (Received) */}
                                <div className="self-start max-w-[85%]">
                                    <div className="bg-gray-800 rounded-2xl rounded-tl-sm px-4 py-2 text-gray-200 text-xs border border-gray-700">
                                        Do you have openings next Tuesday?
                                    </div>
                                    <div className="text-gray-600 text-[9px] mt-1 ml-1">10:42 AM</div>
                                </div>

                                {/* Message 2 (Sent) */}
                                <div className="self-end max-w-[85%] flex flex-col items-end">
                                    <div className="bg-teal-600 rounded-2xl rounded-tr-sm px-4 py-2 text-white text-xs shadow-lg shadow-teal-900/20">
                                        Yes! We have 2:00 PM available.
                                    </div>
                                    <div className="text-gray-600 text-[9px] mt-1 mr-1">10:45 AM</div>
                                </div>

                                {/* Message 3 (Received) - Animated */}
                                {chatState === 'responded' && (
                                    <div className="self-start max-w-[85%] animate-in fade-in slide-in-from-bottom-2 duration-300">
                                        <div className="bg-gray-800 rounded-2xl rounded-tl-sm px-4 py-2 text-gray-200 text-xs border border-gray-700">
                                            Perfect, see you then!
                                        </div>
                                        <div className="text-gray-600 text-[9px] mt-1 ml-1">10:46 AM</div>
                                    </div>
                                )}

                                {/* Typing Indicator */}
                                {chatState === 'typing' && (
                                    <div className="self-start mt-2 animate-in fade-in slide-in-from-bottom-1 duration-200">
                                        <div className="bg-gray-800 rounded-full px-3 py-2 flex gap-1 items-center border border-gray-700 w-12 h-8">
                                            <div className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                            <div className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                            <div className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                                        </div>
                                    </div>
                                )}

                            </div>

                            {/* Input Area */}
                            <div className="p-3 mt-auto border-t border-gray-800">
                                <div className="w-full h-10 bg-gray-900 rounded-full border border-gray-700 flex items-center px-4 justify-between">
                                    <div className="text-gray-600 text-xs">Message...</div>
                                    <div className="w-6 h-6 rounded-full bg-teal-600 flex items-center justify-center">
                                        <Send className="w-3 h-3 text-white" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Floating Elements (Popping out) */}

                    {/* Floating Message Bubble */}
                    <div
                        className="absolute -right-8 bottom-40 w-14 h-14"
                        style={{
                            left: '10%',
                            top: '100%',
                            width: '140px',
                            height: '140px',
                            marginLeft: '-90px',
                            marginTop: '-70px',
                            transform: 'translateZ(130px) rotateZ(-65deg) rotateX(-90deg) rotateY(25deg)', // Angled phone
                            transformStyle: 'preserve-3d'
                        }}
                    >
                        {Array.from({ length: 5 }).map((_, i) => (
                            <div key={i} className="absolute inset-0 rounded-2xl bg-teal-800" style={{ transform: `translateZ(-${i}px)` }} />
                        ))}
                        <div className="absolute inset-0 bg-teal-500 rounded-2xl flex items-center justify-center border border-teal-400 shadow-xl">
                            <MessageSquare className="w-7 h-7 text-white" />
                        </div>
                    </div>

                </div>

                {/* Shadow underneath */}
                <div
                    className="absolute top-0 left-0 w-full h-full rounded-[40px] bg-black/40 blur-3xl z-[-1]"
                    style={{
                        transform: 'translateZ(-60px) scale(0.9)',
                    }}
                />
            </div>
        </div>
    );
};

export default IsometricTwoWayTexting;
